import React, { useState, useEffect, useContext } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { fetchReservationChannelCode, importReservationChannelCode } from '@/apis/aipass'
import { SettingReservationTab } from '@/components/pages/setting/reservation/tab'
import { SettingChannelCodeList } from '@/components/pages/setting/reservation/channel-code/_index/list'
import { ChannelCodeType } from '@/models/reservation-channel-code'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { Popup } from '@/components/atoms/popup'
import { CSVReader } from '@/components/organisms/csv-reader'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'

const parseOptions = {
  header: true,
  skipEmptyLines: true,
}

export const SettingReservationChannelCode: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())

  const [channelCodes, setChannelCodes] = useState<ChannelCodeType[]>([])
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false)
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()

  const onFileLoad = async data => {
    if (!guardSettingsEdit()) return
    try {
      setIsLoading(true)
      await importReservationChannelCode(data)
      fetchReservationChannelCode().then(setChannelCodes)
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchReservationChannelCode().then(setChannelCodes)
  }, [])

  return (
    <SettingsLayout loading={false}>
      <>
        <div css={settingHeaderContainerStyle}>
          <SettingReservationTab currentTab="channelCode" />
        </div>
        <div css={settingListContainerStyle}>
          <div css={editButtonWrapperStyle}>
            <div>
              <Button
                buttonType={isSettingsReadOnly ? ButtonType.PrimaryDisabled : ButtonType.Secondary}
                isDisabled={isSettingsReadOnly}
                onClick={() => {
                  if (!guardSettingsEdit()) return
                  history.push({ pathname: '/setting/reservation/channel-code/edit', search: location.search })
                }}
                width={75}
                height={32}
                fontSize={12}
              >
                {t('Edit')}
              </Button>
            </div>
            <div>
              <div style={{ position: 'relative' }}>
                <Button
                  buttonType={4}
                  width={100}
                  height={32}
                  fontSize={12}
                  icon={require('@/static/images/csv.svg')}
                  onClick={() => setIsDropDownOpen(!isDropDownOpen)}
                >
                  {t('CSV')} <img src={require('@/static/images/select_gray.svg')} css={{ marginLeft: 8 }} />
                </Button>
                <Popup
                  isOpen={isDropDownOpen}
                  onClose={() => setIsDropDownOpen(false)}
                  customStyle={css({ width: 153, right: 0, top: 32 })}
                >
                  <ul>
                    {!isSettingsReadOnly && (
                      <li css={dropDownContentStyle}>
                        <CSVReader
                          inputStyle={{ display: 'none', cursor: 'pointer' }}
                          label={t('Upload')}
                          parserOptions={parseOptions}
                          onFileLoaded={data => {
                            onFileLoad(data)
                            setIsDropDownOpen(false)
                          }}
                        />
                      </li>
                    )}
                  </ul>
                </Popup>
              </div>
            </div>
          </div>
          <SettingChannelCodeList channelCodes={channelCodes} />
        </div>
      </>
    </SettingsLayout>
  )
}

const settingListContainerStyle = css({
  boxShadow: '0px 0px 6px #0000001A',
  borderRadius: '0 5px 5px 5px',
  background: '#FFF',
  position: 'relative',
})

const settingHeaderContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})

const editButtonWrapperStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '24px 32px 19px',
  gap: 16,
})

const dropDownContentStyle = css({
  display: 'flex',
  height: 34,
  listStyle: 'none',
  alignItems: 'center',
  padding: '0 16px',
  cursor: 'pointer',
  '&:hover': {
    background: '#f2f2f2',
  },
  'label, p': {
    fontSize: '14px !important',
    color: '#272727',
  },
})
